import {
    observable,
    action,
    computed,
    makeObservable,
    runInAction,
} from 'mobx';
import { RootStore } from './root';
import { HttpMethod } from '../types';
import {
    AppTarget,
    ApplicationTargetEnum,
    CIRCLE_K_PROVINCES,
    COUCHE_TARD_PROVINCES,
} from '~types/app-selection';
import { kpiEnvelopeSchema } from './kpi-envelope';
import type { KpiEnvelope } from './kpi-envelope';
import { normalizePlatformDownloads } from '~helpers/downloads-composition';
import type {
    DownloadsPlatform,
    NormalizedPlatformDownloads,
} from '~helpers/downloads-composition';
import * as yup from 'yup';
import qs from 'query-string';

const paramsSerializer = (params: Record<string, unknown>) =>
    qs.stringify(params, { encode: true });

/**
 * One endpoint per platform. The combined non-envelope `analytics/app-downloads`
 * this store used to call is gone; these two standardized envelopes replace it
 * (they are the same endpoints the `app-downloads` KPI source fans out to).
 */
const DOWNLOADS_ENDPOINTS: Record<DownloadsPlatform, string> = {
    ios: 'analytics/downloads/ios',
    android: 'analytics/downloads/android',
};

/** Order the funnel reads the two platforms in, and the order reasons join in. */
const DOWNLOADS_PLATFORMS: DownloadsPlatform[] = ['ios', 'android'];

/**
 * The installs figures the acquisition funnel reads, for one period.
 *
 * `total` is null whenever either platform is missing: the funnel presents it as
 * the period's downloads, and a sum amputated of one store would read as the
 * whole. Same rule the KPI cards apply since the endpoint split.
 */
export interface AppDownloadsData {
    /** Both platforms summed, or null when either one could not be served. */
    total: number | null;
    ios: number | null;
    android: number | null;
    /** Why a figure is missing, in the API's own words; null when both served. */
    unavailableReason: string | null;
    /**
     * How far the funnel's installs figure is complete, from each envelope's
     * `dataCompleteThrough`: the older of the two, since the sum stops at the
     * less fresh of the two stores.
     *
     * Null unless BOTH platforms served AND both declared one. A platform that
     * did not serve carries no usable date, and there is no "older of the two"
     * with only one: bounding the card by the surviving platform's date would
     * claim the missing side is complete through it too. The card names that
     * side in its reason line instead, and shows no completeness date at all.
     */
    dataCompleteThrough: string | null;
}

/**
 * The oldest of the two platforms' `dataCompleteThrough` dates, or null as soon
 * as one of them does not declare one (see `AppDownloadsData`). ISO dates, so
 * lexicographic order is chronological order.
 *
 * Read from the NORMALIZED statuses, not from the raw envelopes, so this date
 * comes off the same reading of the pair as the totals and the reasons beside
 * it. An envelope that arrives with a `dataCompleteThrough` but no usable
 * `metrics.total` normalizes to `failed`, and reading it raw put "iOS downloads
 * could not be loaded." next to "Data available until 2026-03-25" above two N/A
 * tiles. Only a platform that actually served has a boundary to declare.
 */
const oldestCompleteThrough = (
    normalized: Record<DownloadsPlatform, NormalizedPlatformDownloads>
): string | null => {
    let oldest: string | null = null;
    for (const platform of DOWNLOADS_PLATFORMS) {
        const data = normalized[platform];
        // A platform that did not serve, or served without declaring a
        // boundary, leaves the pair unbounded: the surviving one's date would
        // claim the missing side is complete through it too.
        if (
            data.status !== 'served' ||
            data.dataCompleteThrough === undefined
        ) {
            return null;
        }
        if (oldest === null || data.dataCompleteThrough < oldest) {
            oldest = data.dataCompleteThrough;
        }
    }
    return oldest;
};

/**
 * Map the two per-platform envelopes into the funnel's installs figures. Pure:
 * fixtures in, funnel shape out, no network and no store.
 *
 * Returns null when NEITHER endpoint produced a usable envelope: there is no
 * data and no API reason to show, which is the blank the funnel already
 * rendered when the legacy call failed. One platform answering is a different
 * story: the step names the side it is missing instead.
 */
export const toAppDownloadsData = (
    envelopes: Record<DownloadsPlatform, KpiEnvelope | null>
): AppDownloadsData | null => {
    const normalized = {
        ios: normalizePlatformDownloads('ios', envelopes.ios),
        android: normalizePlatformDownloads('android', envelopes.android),
    };
    const missing = DOWNLOADS_PLATFORMS.map(
        (platform) => normalized[platform]
    ).filter((data) => data.status !== 'served');
    if (
        missing.length === DOWNLOADS_PLATFORMS.length &&
        missing.every((data) => data.status === 'failed')
    ) {
        return null;
    }

    const totalOf = (platform: DownloadsPlatform): number | null => {
        const data = normalized[platform];
        return data.status === 'served' ? data.total : null;
    };

    return {
        total:
            missing.length > 0
                ? null
                : DOWNLOADS_PLATFORMS.reduce(
                      (sum, platform) => sum + (totalOf(platform) ?? 0),
                      0
                  ),
        ios: totalOf('ios'),
        android: totalOf('android'),
        // Both endpoints resolve geography identically, so a region-driven
        // degradation arrives worded the same on both: dedupe so it reads once.
        unavailableReason:
            missing.length > 0
                ? [...new Set(missing.map((data) => data.reason))].join(' ')
                : null,
        dataCompleteThrough: oldestCompleteThrough(normalized),
    };
};

/**
 * The page's app target as the downloads endpoints understand geography. They
 * take no `appTarget` (the legacy route did): region is a province list resolved
 * through the CT/CK app boundary, so a target maps to its brand's provinces and
 * "all regions" maps to no filter at all.
 */
const provincesForAppTarget = (appTarget?: AppTarget): string | undefined => {
    if (appTarget === ApplicationTargetEnum.QC) {
        return COUCHE_TARD_PROVINCES.join(',');
    }
    if (appTarget === ApplicationTargetEnum.CA) {
        return CIRCLE_K_PROVINCES.join(',');
    }
    return undefined;
};

const onboardingDataSchema = yup.object({
    onboardingStepName: yup.string().required(),
    completedCount: yup.number().required(),
});

const onboardingDataListSchema = yup.object({
    onboardingData: yup.array(onboardingDataSchema).required(),
});

const progressionPeriodMetricsSchema = yup.object({
    status: yup
        .string()
        .oneOf(['IN_PROGRESS', 'COMPLETED', 'NOT_STARTED'])
        .required(),
    total: yup.number().required(),
});

export type OnboardingData = yup.InferType<typeof onboardingDataSchema>;

const segmentationDataSchema = yup.object({
    segmentationId: yup.number().required(),
    name: yup.string().required(),
    recordCount: yup.number().required(),
});

const segmentationDataListSchema = yup.object({
    segmentationData: yup.array(segmentationDataSchema).required(),
});

export type SegmentationData = yup.InferType<typeof segmentationDataSchema>;

const progressionPeriodMetricsListSchema = yup.object({
    progressionData: yup.array(progressionPeriodMetricsSchema).required(),
});

export type ProgressionPeriodMetrics = yup.InferType<
    typeof progressionPeriodMetricsSchema
>;

type OnboardingFetchParams = {
    startDate: string;
    endDate: string;
    appTarget?: AppTarget;
    periodKey?: string;
    provinces?: string;
};

type AppDownloadsComparisonTask = {
    key: string;
    requestId: number;
    currentParams: OnboardingFetchParams;
    previousParams: OnboardingFetchParams;
};

export class OnboardingStore {
    @observable onboardingData: OnboardingData[] = [];
    @observable onboardingDataPrevious: OnboardingData[] = [];
    @observable isFetchingOnboardingData = false;
    @observable segmentationData: SegmentationData[] = [];
    @observable segmentationDataPrevious: SegmentationData[] = [];
    @observable isFetchingSegmentationData = false;
    @observable progressionData: ProgressionPeriodMetrics[] = [];
    @observable progressionDataPrevious: ProgressionPeriodMetrics[] = [];
    @observable isFetchingProgressionData = false;
    @observable appDownloadsData: AppDownloadsData | null = null;
    @observable appDownloadsDataPrevious: AppDownloadsData | null = null;
    @observable private isFetchingCurrentAppDownloads = false;
    @observable private isFetchingPreviousAppDownloads = false;
    @observable private isProcessingAppDownloadsComparison = false;

    @computed
    get isFetchingAppDownloads() {
        return (
            this.isProcessingAppDownloadsComparison ||
            this.isFetchingCurrentAppDownloads ||
            this.isFetchingPreviousAppDownloads
        );
    }

    rootStore: RootStore;

    private appDownloadsRequestId = 0;
    private appDownloadsPreviousRequestId = 0;
    private appDownloadsAbortController: AbortController | null = null;
    private appDownloadsPreviousAbortController: AbortController | null = null;
    private appDownloadsComparisonRequestId = 0;
    private appDownloadsComparisonTask: AppDownloadsComparisonTask | null = null;
    private activeAppDownloadsComparisonKey: string | null = null;
    private queuedAppDownloadsComparisonKey: string | null = null;
    private appDownloadsComparisonPromise: Promise<void> | null = null;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    async fetchSegmentationData(params: OnboardingFetchParams) {
        this.isFetchingSegmentationData = true;

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/onboarding/segmentation/data',
                paramsSerializer,
                params: {
                    startDate: params.startDate,
                    endDate: params.endDate,
                    appTarget: params.appTarget,
                    provinces: params.provinces,
                },
            },
            segmentationDataListSchema
        );

        runInAction(() => {
            this.isFetchingSegmentationData = false;

            if (response.data?.segmentationData) {
                if (params.periodKey === 'previousPeriod') {
                    this.segmentationDataPrevious =
                        response.data.segmentationData;
                } else {
                    this.segmentationData = response.data.segmentationData;
                }
            } else {
                if (params.periodKey === 'previousPeriod') {
                    this.segmentationDataPrevious = [];
                } else {
                    this.segmentationData = [];
                }
            }
        });
    }

    @action
    async fetchOnboardingData(params: OnboardingFetchParams) {
        this.isFetchingOnboardingData = true;

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/onboarding/data',
                paramsSerializer,
                params: {
                    startDate: params.startDate,
                    endDate: params.endDate,
                    appTarget: params.appTarget,
                    provinces: params.provinces,
                },
            },
            onboardingDataListSchema
        );

        runInAction(() => {
            this.isFetchingOnboardingData = false;

            if (response.data?.onboardingData) {
                if (params.periodKey === 'previousPeriod') {
                    this.onboardingDataPrevious = response.data.onboardingData;
                } else {
                    this.onboardingData = response.data.onboardingData;
                }
            } else {
                if (params.periodKey === 'previousPeriod') {
                    this.onboardingDataPrevious = [];
                } else {
                    this.onboardingData = [];
                }
            }
        });
    }

    @action
    async fetchProgressionData(params: OnboardingFetchParams) {
        this.isFetchingProgressionData = true;

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/onboarding/progression',
                paramsSerializer,
                params: {
                    startDate: params.startDate,
                    endDate: params.endDate,
                    appTarget: params.appTarget,
                    provinces: params.provinces,
                },
            },
            progressionPeriodMetricsListSchema
        );

        runInAction(() => {
            this.isFetchingProgressionData = false;

            if (response.data?.progressionData) {
                if (params.periodKey === 'previousPeriod') {
                    this.progressionDataPrevious =
                        response.data.progressionData;
                } else {
                    this.progressionData = response.data.progressionData;
                }
            } else {
                if (params.periodKey === 'previousPeriod') {
                    this.progressionDataPrevious = [];
                } else {
                    this.progressionData = [];
                }
            }
        });
    }

    /**
     * One platform's downloads envelope for a period. Never rejects and never
     * throws: a platform that fails resolves to a null envelope so its sibling's
     * result still reaches the funnel, which is what makes the two stores'
     * failure modes independent.
     */
    private async fetchDownloadsPlatform(
        platform: DownloadsPlatform,
        params: Record<string, string>,
        signal: AbortSignal
    ): Promise<{ envelope: KpiEnvelope | null; cancelled: boolean }> {
        const response = await this.rootStore.makeNetworkCall<unknown>({
            method: HttpMethod.GET,
            url: DOWNLOADS_ENDPOINTS[platform],
            // No `paramsSerializer`, unlike this store's `/onboarding/*` calls:
            // envelope endpoints are queried the same way everywhere in the app
            // (axios defaults), which is what keeps the `provinces` CSV intact
            // for the API's comma-aware query parsing.
            params,
            signal,
        });

        if (response.cancelled) {
            return { envelope: null, cancelled: true };
        }
        // Validated at the boundary against the vendored envelope schema, the
        // same definition the KpiEnvelope type comes from: a drifted body reads
        // back as a platform that could not be loaded rather than a mistyped
        // object.
        const parsed = kpiEnvelopeSchema.safeParse(response.data);
        return {
            envelope: parsed.success ? parsed.data : null,
            cancelled: false,
        };
    }

    @action
    async fetchAppDownloads(params: OnboardingFetchParams) {
        const isPrevious = params.periodKey === 'previousPeriod';

        // One controller for the pair, not one per platform: the two calls are a
        // single logical request for this period, always issued together and
        // always abandoned together, so there is no case where one should
        // outlive the other. A period's controller still stays separate from the
        // other period's, as before.
        const controller = new AbortController();
        if (isPrevious) {
            this.appDownloadsPreviousAbortController?.abort();
            this.appDownloadsPreviousAbortController = controller;
        } else {
            this.appDownloadsAbortController?.abort();
            this.appDownloadsAbortController = controller;
        }

        const requestId = isPrevious
            ? ++this.appDownloadsPreviousRequestId
            : ++this.appDownloadsRequestId;

        if (isPrevious) {
            this.isFetchingPreviousAppDownloads = true;
        } else {
            this.isFetchingCurrentAppDownloads = true;
        }

        const query: Record<string, string> = {
            startDate: params.startDate.split('T')[0],
            endDate: params.endDate.split('T')[0],
            // The funnel shows a period total; only the downloads chart asks for
            // a daily series.
            granularity: 'total',
        };
        const provinces = provincesForAppTarget(params.appTarget);
        if (provinces) {
            query.provinces = provinces;
        }

        // One wave, two independent calls, and a single commit once BOTH have
        // settled. Writing each platform in as it lands would let a filter change
        // leave the funnel showing one store's new number beside the other's old
        // one; the pair is resolved before anything is written, so the period's
        // figures always come from the same request.
        const [ios, android] = await Promise.all([
            this.fetchDownloadsPlatform('ios', query, controller.signal),
            this.fetchDownloadsPlatform('android', query, controller.signal),
        ]);

        runInAction(() => {
            const currentId = isPrevious
                ? this.appDownloadsPreviousRequestId
                : this.appDownloadsRequestId;
            if (requestId !== currentId) return;

            if (isPrevious) {
                this.isFetchingPreviousAppDownloads = false;
            } else {
                this.isFetchingCurrentAppDownloads = false;
            }

            // Either half cancelled means this period's wave was superseded (the
            // comparison queue aborts both controllers before queuing a new
            // task): keep what is on screen instead of blanking it, and never
            // commit a half-cancelled pair.
            if (ios.cancelled || android.cancelled) {
                return;
            }

            const data = toAppDownloadsData({
                ios: ios.envelope,
                android: android.envelope,
            });
            if (isPrevious) {
                this.appDownloadsDataPrevious = data;
            } else {
                this.appDownloadsData = data;
            }
        });
    }

    @action
    fetchAppDownloadsComparison(
        currentParams: OnboardingFetchParams,
        previousParams: OnboardingFetchParams
    ) {
        const key = this.getAppDownloadsComparisonKey(
            currentParams,
            previousParams
        );

        if (
            key === this.activeAppDownloadsComparisonKey ||
            key === this.queuedAppDownloadsComparisonKey
        ) {
            return this.appDownloadsComparisonPromise ?? Promise.resolve();
        }

        const requestId = ++this.appDownloadsComparisonRequestId;
        this.appDownloadsComparisonTask = {
            key,
            requestId,
            currentParams,
            previousParams,
        };
        this.queuedAppDownloadsComparisonKey = key;

        this.appDownloadsAbortController?.abort();
        this.appDownloadsPreviousAbortController?.abort();

        if (!this.isProcessingAppDownloadsComparison) {
            this.appDownloadsComparisonPromise = this.processAppDownloadsComparisonQueue();
        }

        return this.appDownloadsComparisonPromise ?? Promise.resolve();
    }

    private async processAppDownloadsComparisonQueue() {
        runInAction(() => {
            this.isProcessingAppDownloadsComparison = true;
        });

        try {
            while (this.appDownloadsComparisonTask) {
                const task = this.appDownloadsComparisonTask;
                this.appDownloadsComparisonTask = null;
                this.queuedAppDownloadsComparisonKey = null;
                this.activeAppDownloadsComparisonKey = task.key;

                await this.fetchAppDownloads(task.currentParams);

                if (task.requestId !== this.appDownloadsComparisonRequestId) {
                    continue;
                }

                await this.fetchAppDownloads(task.previousParams);
            }
        } finally {
            runInAction(() => {
                this.isProcessingAppDownloadsComparison = false;
            });
            this.activeAppDownloadsComparisonKey = null;
            this.queuedAppDownloadsComparisonKey = null;
            this.appDownloadsComparisonPromise = null;
        }
    }

    private getAppDownloadsComparisonKey(
        currentParams: OnboardingFetchParams,
        previousParams: OnboardingFetchParams
    ) {
        return [
            this.getAppDownloadsRequestKey(currentParams),
            this.getAppDownloadsRequestKey(previousParams),
        ].join('::');
    }

    private getAppDownloadsRequestKey(params: OnboardingFetchParams) {
        return JSON.stringify({
            startDate: params.startDate.split('T')[0],
            endDate: params.endDate.split('T')[0],
            appTarget: params.appTarget ?? null,
            periodKey: params.periodKey ?? 'current',
        });
    }
}
